import type { CodeAttachmentRef } from '@/lib/code-sessions/attachment-types';
import type { CodeSharedFileSummary } from '@/lib/code-sessions/shared-file-types';

import type { CodePromptAttachment, CodeSessionTurn } from './types';

import { codeSessionRequest, jsonBody } from './runtime/cloud';

import {
  codeAttachmentUrl,
  sanitizeAttachmentName,
  turnAttachments,
} from '@/lib/code-sessions/attachment-types';
import { sharedFileSummary, turnSharedFiles } from '@/lib/code-sessions/shared-file-types';

const promptAttachmentsByTurn = new WeakMap<
  object,
  { sessionId: string; attachments: readonly CodePromptAttachment[] }
>();

/**
 * The files a saved turn was sent with, each loading through its session's
 * attachment route, or the files a turn carries itself: the images of a turn on
 * the person's own computer, and the uploads of a send the cloud never got
 * (`CodeSessionTurn.promptAttachments`). The same turn object gives back the
 * same array, so a view refolded on every streamed event neither parses the
 * files again nor redraws them.
 */
export function turnPromptAttachments(
  sessionId: string,
  turn: Pick<CodeSessionTurn, 'attachments' | 'promptAttachments'>,
): readonly CodePromptAttachment[] {
  if (turn.promptAttachments !== undefined) {
    return turn.promptAttachments;
  }
  const cached = promptAttachmentsByTurn.get(turn);
  if (cached?.sessionId === sessionId) {
    return cached.attachments;
  }
  const attachments = turnAttachments(turn.attachments).map(({ id, name, contentType, size }) => ({
    id,
    name,
    contentType,
    size,
    url: codeAttachmentUrl(sessionId, id),
  }));
  promptAttachmentsByTurn.set(turn, { sessionId, attachments });
  return attachments;
}

const sharedFilesByTurn = new WeakMap<object, readonly CodeSharedFileSummary[]>();

/**
 * The files the agent shared during a saved turn, without where the server
 * keeps them; each stored one loads through `codeAttachmentUrl`. The same turn
 * object gives back the same array, as `turnPromptAttachments` does.
 */
export function turnSharedFileSummaries(
  turn: Pick<CodeSessionTurn, 'sharedFiles'>,
): readonly CodeSharedFileSummary[] {
  const cached = sharedFilesByTurn.get(turn);
  if (cached !== undefined) {
    return cached;
  }
  const files = turnSharedFiles(turn.sharedFiles).map(sharedFileSummary);
  sharedFilesByTurn.set(turn, files);
  return files;
}

/**
 * Where a composer's files go: the repository an upload is for, since a session in another one
 * cannot use the file, or this tab, which holds the images a session on the person's own computer
 * sends with its prompt.
 */
export type CodeAttachmentUploadTarget =
  | { kind: 'repository'; installationId: number; repositoryId: number }
  | {
      kind: 'tab';
      /**
       * The room the session has left for images: zero or below once the images it already
       * stores, a tool's included, have used its budget.
       */
      room: number;
      /** What a new image of `size` bytes takes of `room` once stored, by its runtime's rule. */
      cost: (size: number) => number;
    };

/** Types for files a browser leaves untyped, as it can a pasted or dropped one. */
const CONTENT_TYPES_BY_EXTENSION = new Map([
  ['png', 'image/png'],
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
  ['gif', 'image/gif'],
  ['webp', 'image/webp'],
  ['pdf', 'application/pdf'],
]);

/**
 * The type a file uploads as: the browser's, else one its extension names.
 * An upload link is issued for it, and storage refuses a PUT that names any other.
 */
export function uploadContentType(file: File): string {
  if (file.type !== '') {
    return file.type;
  }
  const extension = /\.([^.]+)$/.exec(file.name)?.[1]?.toLowerCase();
  return (
    (extension === undefined ? undefined : CONTENT_TYPES_BY_EXTENSION.get(extension)) ??
    'application/octet-stream'
  );
}

/** An upload that has not finished by then has stalled; it fails, and can be retried. */
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

function putFile(input: {
  url: string;
  file: File;
  contentType: string;
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
}): Promise<void> {
  const { url, file, contentType, signal, onProgress } = input;
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    const settle = (error?: unknown) => {
      signal.removeEventListener('abort', abort);
      if (error === undefined) {
        resolve();
      } else {
        reject(error);
      }
    };
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    });
    request.addEventListener('load', () =>
      settle(
        request.status >= 200 && request.status < 300
          ? undefined
          : new Error(`Could not upload (HTTP ${request.status})`),
      ),
    );
    request.addEventListener('error', () => settle(new Error('Could not upload')));
    request.addEventListener('timeout', () => settle(new Error('Upload timed out')));
    request.addEventListener('abort', () => settle(signal.reason));
    request.open('PUT', url);
    request.timeout = UPLOAD_TIMEOUT_MS;
    request.setRequestHeader('content-type', contentType);
    request.send(file);
  });
}

/**
 * Upload one file for a prompt: ask for an upload link, then PUT the file
 * straight to storage. The PUT uses XMLHttpRequest because fetch reports no
 * upload progress. Resolves with what the prompt sends for the file.
 */
export async function uploadCodeAttachment(input: {
  file: File;
  target: Extract<CodeAttachmentUploadTarget, { kind: 'repository' }>;
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
}): Promise<CodeAttachmentRef> {
  const { file, target, signal, onProgress } = input;
  const contentType = uploadContentType(file);
  const { attachment, uploadUrl } = await codeSessionRequest<{
    attachment: CodeAttachmentRef;
    uploadUrl: string;
  }>('/api/code-sessions/attachments', {
    ...jsonBody({
      installationId: target.installationId,
      repositoryId: target.repositoryId,
      name: sanitizeAttachmentName(file.name),
      contentType,
      size: file.size,
    }),
    signal,
  });
  await putFile({ url: uploadUrl, file, contentType, signal, onProgress });
  return attachment;
}
